import {
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormLabel,
  Heading,
  Select,
  SimpleGrid,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@uirouter/react";
import React from "react";
import { Messages, ResponseOf } from "../../../../core/api";
import Page from "../../../../shared/components/Page";
import { useEntityLink } from "../../../../shared/hooks/useEntityLink";
import useApi from "../../../../shared/hooks/useApi";
import { PayrollBatchId } from "../../../../shared/schema/schema";
import { currencyFormatter } from "../../../../shared/utils/currency-formatter";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import PayrollAuditChange from "../../components/PayrollAuditChange";
import PayrollAuditDistributionChart from "../../components/PayrollAuditDistributionChart";

type ServiceType = Messages["PayrollAuditServiceType"];
type PayrollAudit = ResponseOf<"get", "./payroll_audit/:payrollBatchId">;

const serviceTypeOptions: { value: ServiceType; label: string }[] = [
  { value: "HOURLY", label: "Hourly" },
  { value: "DAILY", label: "Daily" },
  { value: "VISIT", label: "Visit" },
  { value: "CLUSTER", label: "Cluster" },
];

const hoursFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function formatHours(seconds: number) {
  return `${hoursFormatter.format(seconds / 3600)} hrs`;
}

const wholeDollarsFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function formatWholeDollars(cents: number) {
  return wholeDollarsFormatter.format(cents / 100);
}

function formatCount(value: number) {
  return hoursFormatter.format(value);
}

function serviceTypeLabel(serviceType: ServiceType) {
  return serviceTypeOptions.find((option) => option.value === serviceType)?.label ?? serviceType;
}

function rateUnit(serviceType: ServiceType) {
  switch (serviceType) {
    case "HOURLY":
    case "CLUSTER":
      return "/ hr";
    case "DAILY":
      return "/ day";
    case "VISIT":
      return "/ visit";
  }
}

function batchLabel(batch: { displayId: string; date: PayrollAudit["date"] }) {
  return `Batch ${batch.displayId} (${dateFormatter.toDate(batch.date)})`;
}

type Distribution = "caregiverAmounts" | "caregiverSeconds" | "caregiverVisits" | "visitAmounts";

const distributionOptions: {
  value: Distribution;
  label: string;
  format: (value: number) => string;
  formatTick?: (value: number) => string;
  minBinWidth?: number;
}[] = [
  {
    value: "caregiverAmounts",
    label: "Payout per caregiver",
    format: currencyFormatter.formatCents,
    formatTick: formatWholeDollars,
  },
  { value: "caregiverSeconds", label: "Hours per caregiver", format: formatHours },
  { value: "caregiverVisits", label: "Visits per caregiver", format: formatCount, minBinWidth: 1 },
  {
    value: "visitAmounts",
    label: "Payout per visit",
    format: currencyFormatter.formatCents,
    formatTick: formatWholeDollars,
  },
];

/** The batch being compared against, when comparison mode is on. */
type Comparison = {
  audit: PayrollAudit;
  label: string;
};

type Props = {
  payrollBatchId: PayrollBatchId;
};

const PayrollAuditPage = (props: Props) => {
  const { queries } = useApi();
  const { stateService } = useRouter();
  const [serviceTypes, setServiceTypes] = React.useState<ServiceType[]>(
    serviceTypeOptions.map((option) => option.value)
  );
  const [compareBatchId, setCompareBatchId] = React.useState<PayrollBatchId | null>(null);

  const query = useQuery({
    ...queries.payroll.payrollAudit(props.payrollBatchId, serviceTypes),
    enabled: serviceTypes.length > 0,
    placeholderData: (previous) => previous,
  });

  const compareQuery = useQuery({
    ...queries.payroll.payrollAudit(compareBatchId ?? props.payrollBatchId, serviceTypes),
    enabled: serviceTypes.length > 0 && compareBatchId !== null,
  });

  const comparableBatches = query.data?.comparableBatches ?? [];
  const compareBatch = comparableBatches.find((batch) => batch.payrollBatchId === compareBatchId);
  const comparison: Comparison | null =
    compareBatch !== undefined && compareQuery.data !== undefined
      ? { audit: compareQuery.data, label: batchLabel(compareBatch) }
      : null;

  return (
    <Page isLoading={query.isFetching || compareQuery.isFetching}>
      <Page.Header>
        <Flex alignItems="center" gap={3}>
          <Heading size="lg">Payroll Audit for batch {String(props.payrollBatchId)}</Heading>
          {query.data?.isDraft === true ? <Badge colorScheme="red">Draft</Badge> : null}
        </Flex>
        <Button
          size="sm"
          variant="outline"
          onClick={() => stateService.go("app.payroll.batch", { id: props.payrollBatchId })}
        >
          Back to batch
        </Button>
      </Page.Header>
      <Page.Filters alignItems="center" flexWrap="wrap" gap={4}>
        <Text fontWeight="semibold">Service types</Text>
        <CheckboxGroup
          value={serviceTypes}
          onChange={(values) => {
            // Keep the fixed order so the query key does not depend on click order.
            setServiceTypes(
              serviceTypeOptions
                .map((option) => option.value)
                .filter((value) => values.includes(value))
            );
          }}
        >
          <Flex gap={4}>
            {serviceTypeOptions.map((option) => (
              <Checkbox key={option.value} value={option.value}>
                {option.label}
              </Checkbox>
            ))}
          </Flex>
        </CheckboxGroup>
        <Flex alignItems="center" gap={2} ml={{ base: 0, xl: 6 }}>
          <Text fontWeight="semibold" whiteSpace="nowrap">
            Compare with
          </Text>
          <Select
            isDisabled={comparableBatches.length === 0}
            size="sm"
            value={compareBatchId === null ? "" : String(compareBatchId)}
            width="260px"
            onChange={(event) =>
              setCompareBatchId(
                event.currentTarget.value === ""
                  ? null
                  : PayrollBatchId.parse(Number(event.currentTarget.value))
              )
            }
          >
            <option value="">
              {comparableBatches.length === 0 ? "No earlier batches" : "No comparison"}
            </option>
            {comparableBatches.map((batch) => (
              <option key={batch.payrollBatchId} value={String(batch.payrollBatchId)}>
                {batchLabel(batch)}
              </option>
            ))}
          </Select>
        </Flex>
        <Text color="gray.500" fontSize="sm">
          Visit pay only. PTO, travel time and trainings are not included.
        </Text>
      </Page.Filters>
      <Page.Content>
        {(() => {
          if (serviceTypes.length === 0) {
            return <EmptyState text="Select at least one service type" />;
          }
          if (query.isPending) {
            return <EmptyState text="Loading..." />;
          }
          if (query.isError) {
            return <EmptyState text="Could not load the payroll audit" />;
          }
          return (
            <Flex direction="column" gap={4}>
              {compareQuery.isError && compareBatchId !== null ? (
                <Text color="red.600" fontSize="sm">
                  Could not load the batch to compare with
                </Text>
              ) : null}
              {comparison === null ? null : (
                <Text color="gray.600" fontSize="sm">
                  Changes in brackets are compared with {comparison.label}.
                </Text>
              )}
              <PayrollAuditContent audit={query.data} comparison={comparison} />
            </Flex>
          );
        })()}
      </Page.Content>
    </Page>
  );
};

function EmptyState(props: { text: string }) {
  return (
    <Text color="gray.500" py={10} textAlign="center">
      {props.text}
    </Text>
  );
}

function averagePerCaregiver(totals: Messages["PayrollAuditTotals"]) {
  return totals.caregivers === 0 ? null : totals.amount / totals.caregivers;
}

function PayrollAuditContent(props: { audit: PayrollAudit; comparison: Comparison | null }) {
  const { audit, comparison } = props;
  const previous = comparison?.audit ?? null;
  const entityLink = useEntityLink();
  const topCaregiver = audit.topCaregivers.at(0);
  const previousTopCaregiver = previous?.topCaregivers.at(0);
  const average = averagePerCaregiver(audit.totals);
  const previousAverage = previous === null ? null : averagePerCaregiver(previous.totals);

  return (
    <Flex direction="column" gap={6}>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 5 }} gap={4}>
        <MetricTile
          change={
            previous === null ? undefined : (
              <PayrollAuditChange current={audit.totals.amount} previous={previous.totals.amount} />
            )
          }
          hint={
            previous === null
              ? `${audit.totals.visits.toLocaleString()} visits, ${formatHours(
                  audit.totals.seconds
                )}`
              : `vs ${currencyFormatter.formatCents(previous.totals.amount)}`
          }
          label="Total visit pay"
          value={currencyFormatter.formatCents(audit.totals.amount)}
        />
        <MetricTile
          change={
            previous === null || average === null || previousAverage === null ? undefined : (
              <PayrollAuditChange current={average} previous={previousAverage} />
            )
          }
          hint={
            previous === null || previousAverage === null
              ? `${audit.totals.caregivers.toLocaleString()} caregivers`
              : `vs ${currencyFormatter.formatCents(previousAverage)}`
          }
          label="Average payout per caregiver"
          value={average === null ? "-" : currencyFormatter.formatCents(average)}
        />
        <MetricTile
          change={
            topCaregiver === undefined || previousTopCaregiver === undefined ? undefined : (
              <PayrollAuditChange
                current={topCaregiver.amount}
                previous={previousTopCaregiver.amount}
              />
            )
          }
          hint={
            previousTopCaregiver === undefined
              ? topCaregiver?.caregiverName
              : `${topCaregiver?.caregiverName ?? "-"} · vs ${currencyFormatter.formatCents(
                  previousTopCaregiver.amount
                )}`
          }
          label="Highest caregiver total"
          value={
            topCaregiver === undefined ? "-" : currencyFormatter.formatCents(topCaregiver.amount)
          }
          onClick={
            topCaregiver === undefined
              ? undefined
              : (event) =>
                  entityLink.open({
                    event,
                    entity: { type: "Caregiver", id: topCaregiver.caregiverId },
                  })
          }
        />
        <MetricTile
          hint={
            previous !== null
              ? `vs ${
                  previous.oldestVisit === null
                    ? "none"
                    : dateFormatter.toDate(previous.oldestVisit.startTime)
                }`
              : audit.oldestVisit === null
              ? undefined
              : `${audit.oldestVisit.caregiverName} · ${audit.oldestVisit.patientName}`
          }
          label="Oldest visit"
          value={
            audit.oldestVisit === null ? "-" : dateFormatter.toDate(audit.oldestVisit.startTime)
          }
          onClick={
            audit.oldestVisit === null
              ? undefined
              : (event) =>
                  entityLink.open({
                    event,
                    entity: { type: "VisitInstance", id: audit.oldestVisit!.visitInstanceId },
                  })
          }
        />
        <MetricTile
          change={
            previous === null ? undefined : (
              <PayrollAuditChange
                current={audit.repaidVisits.length}
                previous={previous.repaidVisits.length}
              />
            )
          }
          hint={
            previous === null
              ? "Paid to 0 before, paid again now"
              : `vs ${previous.repaidVisits.length.toLocaleString()}`
          }
          label="Reversed & repaid visits"
          tone={audit.repaidVisits.length > 0 ? "warning" : "default"}
          value={audit.repaidVisits.length.toLocaleString()}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} gap={6}>
        <AuditCard
          hint={`Old = visits before the batch week start (${dateFormatter.toDate(
            audit.weekStart
          )})`}
          title="Current period vs old period"
        >
          <PeriodTable audit={audit} previous={previous} />
        </AuditCard>
        <AuditCard hint="By the visit's payroll code" title="Payroll codes">
          <PayrollCodesTable audit={audit} previous={previous} />
        </AuditCard>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} gap={6}>
        <AuditCard
          hint="Paid regular and live-in regular lines; most common rates under each type. Overtime, holiday and spread of hours are excluded."
          title="Rate range of non-overtime visits"
        >
          <RateRangeTable previous={previous} rateRanges={audit.nonOvertimeRates} />
        </AuditCard>
        <AuditCard
          hint={
            previous === null
              ? "By total visit pay in this batch"
              : "By total visit pay in this batch, compared with the same caregiver"
          }
          title="Highest paid caregivers"
        >
          <TopCaregiversTable audit={audit} previous={previous} />
        </AuditCard>
      </SimpleGrid>

      <DistributionCard audit={audit} comparison={comparison} />

      <AuditCard
        hint="Visits that had a non-zero payment before this batch, whose earlier payments summed to 0, and that are paid again in this batch"
        title="Reversed visits being repaid"
      >
        <RepaidVisitsTable audit={audit} />
      </AuditCard>
    </Flex>
  );
}

/** A value with its change against the comparison batch, if there is one. */
function ValueWithChange(props: {
  value: React.ReactNode;
  current: number;
  previous: number | null | undefined;
  hasComparison: boolean;
  mode?: "percent" | "points";
}) {
  // The change goes on its own line so comparison mode does not widen the tables.
  return (
    <>
      <Box>{props.value}</Box>
      {props.hasComparison ? (
        <Box lineHeight={1.2}>
          <PayrollAuditChange
            current={props.current}
            mode={props.mode}
            previous={props.previous ?? 0}
          />
        </Box>
      ) : null}
    </>
  );
}

function PeriodTable(props: { audit: PayrollAudit; previous: PayrollAudit | null }) {
  const rows: {
    label: string;
    totals: Messages["PayrollAuditTotals"];
    previous: Messages["PayrollAuditTotals"] | undefined;
  }[] = [
    {
      label: "Current period",
      totals: props.audit.currentPeriod,
      previous: props.previous?.currentPeriod,
    },
    { label: "Old period", totals: props.audit.oldPeriod, previous: props.previous?.oldPeriod },
    { label: "Total", totals: props.audit.totals, previous: props.previous?.totals },
  ];
  const hasComparison = props.previous !== null;

  const shareOf = (amount: number, total: number) => (total === 0 ? 0 : (amount / total) * 100);

  return (
    <TableContainer>
      <Table size="sm">
        <Thead>
          <Tr>
            <Th></Th>
            <Th isNumeric>Visits</Th>
            <Th isNumeric>Caregivers</Th>
            <Th isNumeric>Hours</Th>
            <Th isNumeric>Amount</Th>
            <Th isNumeric>% of amount</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr key={row.label} fontWeight={row.label === "Total" ? "semibold" : undefined}>
              <Td>{row.label}</Td>
              <Td isNumeric>
                <ValueWithChange
                  current={row.totals.visits}
                  hasComparison={hasComparison}
                  previous={row.previous?.visits}
                  value={row.totals.visits.toLocaleString()}
                />
              </Td>
              <Td isNumeric>
                <ValueWithChange
                  current={row.totals.caregivers}
                  hasComparison={hasComparison}
                  previous={row.previous?.caregivers}
                  value={row.totals.caregivers.toLocaleString()}
                />
              </Td>
              <Td isNumeric>
                <ValueWithChange
                  current={row.totals.seconds}
                  hasComparison={hasComparison}
                  previous={row.previous?.seconds}
                  value={formatHours(row.totals.seconds)}
                />
              </Td>
              <Td isNumeric>
                <ValueWithChange
                  current={row.totals.amount}
                  hasComparison={hasComparison}
                  previous={row.previous?.amount}
                  value={currencyFormatter.formatCents(row.totals.amount)}
                />
              </Td>
              <Td isNumeric>
                {props.audit.totals.amount === 0 ? (
                  "-"
                ) : (
                  <ValueWithChange
                    current={shareOf(row.totals.amount, props.audit.totals.amount)}
                    hasComparison={hasComparison && row.label !== "Total"}
                    mode="points"
                    previous={
                      props.previous === null || row.previous === undefined
                        ? null
                        : shareOf(row.previous.amount, props.previous.totals.amount)
                    }
                    value={`${
                      Math.round(shareOf(row.totals.amount, props.audit.totals.amount) * 10) / 10
                    }%`}
                  />
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function PayrollCodesTable(props: { audit: PayrollAudit; previous: PayrollAudit | null }) {
  const hasComparison = props.previous !== null;
  const previousByCode = new Map(
    (props.previous?.payrollCodes ?? []).map((code) => [code.payrollCodeId, code])
  );
  // Codes that were only in the compared batch still show, at zero.
  const onlyInPrevious = (props.previous?.payrollCodes ?? []).filter(
    (code) => !props.audit.payrollCodes.some((c) => c.payrollCodeId === code.payrollCodeId)
  );
  const rows = [
    ...props.audit.payrollCodes,
    ...onlyInPrevious.map((code) => ({ ...code, visits: 0, caregivers: 0, seconds: 0, amount: 0 })),
  ];

  return (
    <TableContainer>
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Payroll code</Th>
            <Th isNumeric>Visits</Th>
            <Th isNumeric>Caregivers</Th>
            <Th isNumeric>Hours</Th>
            <Th isNumeric>Amount</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((code) => {
            const previous = previousByCode.get(code.payrollCodeId);
            return (
              <Tr key={code.payrollCodeId ?? "none"}>
                <Td>{code.payrollCodeName}</Td>
                <Td isNumeric>
                  <ValueWithChange
                    current={code.visits}
                    hasComparison={hasComparison}
                    previous={previous?.visits}
                    value={code.visits.toLocaleString()}
                  />
                </Td>
                <Td isNumeric>
                  <ValueWithChange
                    current={code.caregivers}
                    hasComparison={hasComparison}
                    previous={previous?.caregivers}
                    value={code.caregivers.toLocaleString()}
                  />
                </Td>
                <Td isNumeric>
                  <ValueWithChange
                    current={code.seconds}
                    hasComparison={hasComparison}
                    previous={previous?.seconds}
                    value={formatHours(code.seconds)}
                  />
                </Td>
                <Td isNumeric>
                  <ValueWithChange
                    current={code.amount}
                    hasComparison={hasComparison}
                    previous={previous?.amount}
                    value={currencyFormatter.formatCents(code.amount)}
                  />
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function RateRangeTable(props: {
  rateRanges: Messages["PayrollAuditRateRange"][];
  previous: PayrollAudit | null;
}) {
  if (props.rateRanges.length === 0) {
    return <EmptyState text="No regular pay lines" />;
  }

  const hasComparison = props.previous !== null;

  return (
    <TableContainer>
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Service type</Th>
            <Th isNumeric>Lines</Th>
            <Th isNumeric>Min</Th>
            <Th isNumeric>Average</Th>
            <Th isNumeric>Max</Th>
          </Tr>
        </Thead>
        <Tbody>
          {props.rateRanges.map((range) => {
            const unit = rateUnit(range.serviceType);
            const previous = props.previous?.nonOvertimeRates.find(
              (r) => r.serviceType === range.serviceType
            );
            const commonRates = [...range.rates].sort((a, b) => b[1] - a[1]).slice(0, 4);

            return (
              <Tr key={range.serviceType}>
                <Td>
                  <Box>{serviceTypeLabel(range.serviceType)}</Box>
                  <Box
                    color="gray.500"
                    fontSize="xs"
                    maxW="150px"
                    title="Most common rates"
                    whiteSpace="normal"
                  >
                    {commonRates.map(([rate, lines], index) => (
                      <React.Fragment key={rate}>
                        {index > 0 ? ", " : null}
                        <Text as="span" whiteSpace="nowrap">
                          {currencyFormatter.formatCents(rate)} ×{lines}
                        </Text>
                      </React.Fragment>
                    ))}
                  </Box>
                </Td>
                <Td isNumeric>
                  <ValueWithChange
                    current={range.lines}
                    hasComparison={hasComparison}
                    previous={previous?.lines}
                    value={range.lines.toLocaleString()}
                  />
                </Td>
                <Td isNumeric>
                  <ValueWithChange
                    current={range.minRate}
                    hasComparison={hasComparison}
                    previous={previous?.minRate}
                    value={`${currencyFormatter.formatCents(range.minRate)} ${unit}`}
                  />
                </Td>
                <Td isNumeric>
                  <ValueWithChange
                    current={range.avgRate}
                    hasComparison={hasComparison}
                    previous={previous?.avgRate}
                    value={`${currencyFormatter.formatCents(range.avgRate)} ${unit}`}
                  />
                </Td>
                <Td isNumeric>
                  <ValueWithChange
                    current={range.maxRate}
                    hasComparison={hasComparison}
                    previous={previous?.maxRate}
                    value={`${currencyFormatter.formatCents(range.maxRate)} ${unit}`}
                  />
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function TopCaregiversTable(props: { audit: PayrollAudit; previous: PayrollAudit | null }) {
  const entityLink = useEntityLink();
  const hasComparison = props.previous !== null;
  const previousByCaregiver = new Map(
    (props.previous?.caregiverTotals ?? []).map((caregiver) => [caregiver.caregiverId, caregiver])
  );

  return (
    <TableContainer>
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Caregiver</Th>
            <Th isNumeric>Visits</Th>
            <Th isNumeric>Hours</Th>
            <Th isNumeric>Amount</Th>
          </Tr>
        </Thead>
        <Tbody>
          {props.audit.topCaregivers.map((caregiver) => {
            const previous = previousByCaregiver.get(caregiver.caregiverId);
            return (
              <Tr key={caregiver.caregiverId}>
                <Td>
                  <EntityAnchor
                    onClick={(event) =>
                      entityLink.open({
                        event,
                        entity: { type: "Caregiver", id: caregiver.caregiverId },
                      })
                    }
                  >
                    {caregiver.caregiverName}
                    {caregiver.caregiverDisplayId === null
                      ? ""
                      : ` (${caregiver.caregiverDisplayId})`}
                  </EntityAnchor>
                </Td>
                <Td isNumeric>
                  <ValueWithChange
                    current={caregiver.visits}
                    hasComparison={hasComparison}
                    previous={previous?.visits}
                    value={caregiver.visits.toLocaleString()}
                  />
                </Td>
                <Td isNumeric>
                  <ValueWithChange
                    current={caregiver.seconds}
                    hasComparison={hasComparison}
                    previous={previous?.seconds}
                    value={formatHours(caregiver.seconds)}
                  />
                </Td>
                <Td isNumeric>
                  <ValueWithChange
                    current={caregiver.amount}
                    hasComparison={hasComparison}
                    previous={previous?.amount}
                    value={currencyFormatter.formatCents(caregiver.amount)}
                  />
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function RepaidVisitsTable(props: { audit: PayrollAudit }) {
  const entityLink = useEntityLink();

  if (props.audit.repaidVisits.length === 0) {
    return <EmptyState text="None in this batch" />;
  }

  return (
    <TableContainer>
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Visit date</Th>
            <Th>Caregiver</Th>
            <Th>Patient</Th>
            <Th>Service type</Th>
            <Th isNumeric>Paid before (reversed)</Th>
            <Th isNumeric>Paid now</Th>
            <Th isNumeric>Hours now</Th>
            <Th>Earlier batches</Th>
          </Tr>
        </Thead>
        <Tbody>
          {props.audit.repaidVisits.map((visit) => (
            <Tr key={visit.visitInstanceId}>
              <Td>
                <EntityAnchor
                  onClick={(event) =>
                    entityLink.open({
                      event,
                      entity: { type: "VisitInstance", id: visit.visitInstanceId },
                    })
                  }
                >
                  {dateFormatter.toDate(visit.startTime)}
                </EntityAnchor>
              </Td>
              <Td>
                <EntityAnchor
                  onClick={(event) =>
                    entityLink.open({
                      event,
                      entity: { type: "Caregiver", id: visit.caregiverId },
                    })
                  }
                >
                  {visit.caregiverName}
                </EntityAnchor>
              </Td>
              <Td>
                <EntityAnchor
                  onClick={(event) =>
                    entityLink.open({ event, entity: { type: "Patient", id: visit.patientId } })
                  }
                >
                  {visit.patientName}
                </EntityAnchor>
              </Td>
              <Td>{serviceTypeLabel(visit.serviceType)}</Td>
              <Td isNumeric>{currencyFormatter.formatCents(visit.previouslyPaidAmount)}</Td>
              <Td isNumeric>{currencyFormatter.formatCents(visit.amount)}</Td>
              <Td isNumeric>{formatHours(visit.seconds)}</Td>
              <Td>{visit.previousBatchIds.join(", ")}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function DistributionCard(props: { audit: PayrollAudit; comparison: Comparison | null }) {
  const [distribution, setDistribution] = React.useState<Distribution>("caregiverAmounts");
  const [hideOutliers, setHideOutliers] = React.useState(true);
  const switchId = React.useId();
  const option = distributionOptions.find((option) => option.value === distribution)!;

  return (
    <AuditCard
      actions={
        <Flex alignItems="center" gap={4}>
          <Flex alignItems="center">
            <FormLabel fontSize="sm" htmlFor={switchId} mb={0}>
              Hide outliers
            </FormLabel>
            <Switch
              id={switchId}
              isChecked={hideOutliers}
              onChange={(event) => setHideOutliers(event.currentTarget.checked)}
            />
          </Flex>
          <Select
            size="sm"
            value={distribution}
            width="220px"
            onChange={(event) => setDistribution(event.currentTarget.value as Distribution)}
          >
            {distributionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Flex>
      }
      hint="Histogram with the normal curve of the same mean and standard deviation"
      title="Distribution"
    >
      <PayrollAuditDistributionChart
        compareLabel={props.comparison?.label}
        compareValues={props.comparison?.audit.distributions[distribution]}
        formatTick={option.formatTick}
        formatValue={option.format}
        hideOutliers={hideOutliers}
        minBinWidth={option.minBinWidth}
        values={props.audit.distributions[distribution]}
      />
    </AuditCard>
  );
}

function MetricTile(props: {
  label: string;
  value: React.ReactNode;
  change?: React.ReactNode;
  hint?: string;
  tone?: "default" | "warning";
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}) {
  const isWarning = props.tone === "warning";

  return (
    <Box
      _hover={props.onClick === undefined ? undefined : { borderColor: "blue.300" }}
      as={props.onClick === undefined ? "div" : "button"}
      bg={isWarning ? "orange.50" : "gray.50"}
      borderColor={isWarning ? "orange.200" : "transparent"}
      borderRadius="md"
      borderWidth={1}
      cursor={props.onClick === undefined ? "default" : "pointer"}
      px={4}
      py={3}
      textAlign="left"
      onClick={props.onClick}
    >
      <Text color="gray.600" fontSize="sm">
        {props.label}
      </Text>
      <Flex alignItems="baseline" flexWrap="wrap" gap={1}>
        <Text color="gray.900" fontSize="2xl" fontWeight="semibold">
          {props.value}
        </Text>
        {props.change === undefined ? null : <Box fontSize="md">{props.change}</Box>}
      </Flex>
      {props.hint === undefined ? null : (
        <Text color="gray.500" fontSize="xs" noOfLines={1}>
          {props.hint}
        </Text>
      )}
    </Box>
  );
}

function AuditCard(props: {
  title: string;
  hint?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box borderColor="gray.200" borderRadius="md" borderWidth={1} p={4}>
      <Flex alignItems="flex-start" gap={4} justifyContent="space-between" mb={3}>
        <Box>
          <Heading size="sm">{props.title}</Heading>
          {props.hint === undefined ? null : (
            <Text color="gray.500" fontSize="xs">
              {props.hint}
            </Text>
          )}
        </Box>
        {props.actions}
      </Flex>
      {props.children}
    </Box>
  );
}

function EntityAnchor(props: {
  children: React.ReactNode;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}) {
  return (
    <Text
      as="a"
      color="blue.600"
      cursor="pointer"
      textDecoration="underline"
      onClick={props.onClick}
    >
      {props.children}
    </Text>
  );
}

export default PayrollAuditPage;
